import { RecordingSpeakerRoleType } from "@repo/app-types/entities/recording";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import {
  TranscriptSpeakerAvatar,
  type TranscriptSpeakerAvatarProps,
} from "./transcriptSpeakerAvatar.js";

export type TextMessageProps = TranscriptSpeakerAvatarProps & {
  timestamp: ReactNode;
  children: ReactNode;
};

/** A transcript message drawn as a texting bubble. */
export const TextMessage: FC<TextMessageProps> = (props) => {
  const { timestamp, children, ...speakerProps } = props;
  const { speakerName, speakerRole } = speakerProps;
  const isOutgoing = speakerRole === RecordingSpeakerRoleType.Representative;

  return (
    <div
      className={twMerge(
        "grid max-w-[65%] gap-x-3",
        isOutgoing
          ? "ml-auto grid-cols-[minmax(0,1fr)_2rem]"
          : "grid-cols-[2rem_minmax(0,1fr)]",
      )}
      data-transcript-message
    >
      <div
        className={twMerge(
          "row-start-1 flex w-fit max-w-full min-w-0 flex-col gap-y-1.5",
          isOutgoing ? "col-start-1 justify-self-end" : "col-start-2",
        )}
      >
        <div
          className={twMerge(
            "flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1 px-4",
            isOutgoing && "flex-row-reverse",
          )}
        >
          <span className="min-w-0 text-xs font-medium wrap-anywhere">
            {speakerName}
          </span>
          <span className="shrink-0 text-2xs font-normal text-muted-foreground select-none">
            {timestamp}
          </span>
        </div>
        <div className="relative isolate min-w-0 px-4 py-3 text-sm leading-relaxed wrap-anywhere">
          {children}
          {/* After the text, so scrolling to a message's text keeps its header in view. */}
          <BubbleSurface isOutgoing={isOutgoing} />
        </div>
      </div>
      <div
        className={twMerge(
          "row-start-1 translate-y-2 self-end",
          isOutgoing ? "col-start-2" : "col-start-1",
        )}
      >
        <TranscriptSpeakerAvatar {...speakerProps} />
      </div>
    </div>
  );
};

interface BubbleSurfaceProps {
  isOutgoing: boolean;
}

const BubbleSurface: FC<BubbleSurfaceProps> = (props) => {
  const { isOutgoing } = props;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-1"
    >
      {/* The curved hook fills the rounded corner by the avatar. */}
      <svg
        viewBox="0 0 32 32"
        className={twMerge(
          "absolute -bottom-2 size-8 fill-card",
          isOutgoing ? "right-0 -scale-x-100" : "left-0",
        )}
      >
        <path d="M0 0C0 9 3 14 7 19C10 23 9 27 5 30C4 31 5 32 7 31C14 29 20 25 26 24H32V0Z" />
      </svg>
      <div className="absolute inset-0 rounded-[18px] bg-card" />
    </div>
  );
};
